import { useState, useEffect, useCallback } from 'react';
import { getGuests, getRooms, createBooking, createGroupBooking, createGuest } from '../../lib/api';
import { useAuth } from '../../context/AuthContext';
import { GuestFormDialog } from '../guests/GuestFormDialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Calendar } from '../ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import { Switch } from '../ui/switch';
import { Checkbox } from '../ui/checkbox';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../ui/select';
import { Calendar as CalendarIcon, Zap, UserPlus, ShieldCheck, Plus, Users } from 'lucide-react';
import { toast } from 'sonner';
import { format, addDays } from 'date-fns';

const MEAL_PLAN_OPTIONS = [
  { value: 'room_only', label: 'Room Only' },
  { value: 'breakfast', label: 'Breakfast' },
  { value: 'breakfast_lunch', label: 'BF + Lunch' },
  { value: 'breakfast_dinner', label: 'BF + Dinner' },
  { value: 'all_meals', label: 'All Meals' },
];

const emptyGuestForm = {
  name: '', email: '', phone: '', id_type: 'aadhaar', id_number: '',
  address: '', nationality: 'Indian', is_foreign: false,
};

export const QuickBookingBar = ({ onBooked }) => {
  const { isAdmin } = useAuth();
  const [guests, setGuests] = useState([]);
  const [rooms, setRooms] = useState([]);
  const [groupMode, setGroupMode] = useState(false);
  const [guestId, setGuestId] = useState('');
  const [roomId, setRoomId] = useState('');           // single-room mode
  const [roomIds, setRoomIds] = useState([]);          // group mode
  const [groupName, setGroupName] = useState('');
  const [numGuestsPerRoom, setNumGuestsPerRoom] = useState(1);
  const [checkIn, setCheckIn] = useState(null);
  const [checkOut, setCheckOut] = useState(null);
  const [couponCode, setCouponCode] = useState('');
  const [mealPlan, setMealPlan] = useState('room_only');
  const [customRate, setCustomRate] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const [guestDialogOpen, setGuestDialogOpen] = useState(false);
  const [guestForm, setGuestForm] = useState(emptyGuestForm);
  const [creatingGuest, setCreatingGuest] = useState(false);

  const loadOptions = useCallback(async () => {
    try {
      const [g, r] = await Promise.all([getGuests(), getRooms()]);  // all rooms — conflicts checked server-side per date range
      setGuests(g.data);
      setRooms(r.data);
    } catch {
      // silently ignore
    }
  }, []);

  useEffect(() => { loadOptions(); }, [loadOptions]);

  const reset = () => {
    setGuestId('');
    setRoomId('');
    setRoomIds([]);
    setGroupName('');
    setNumGuestsPerRoom(1);
    setCheckIn(null);
    setCheckOut(null);
    setCouponCode('');
    setMealPlan('room_only');
    setCustomRate('');
  };

  const handleCheckInChange = (date) => {
    if (!date) return;
    setCheckIn(date);
    if (!checkOut || checkOut <= date) setCheckOut(addDays(date, 1));
  };

  const toggleGroupMode = (val) => {
    setGroupMode(val);
    // reset room selection when switching modes
    setRoomId('');
    setRoomIds([]);
  };

  const toggleRoomInGroup = (id) => {
    setRoomIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  };

  const openNewGuestDialog = () => {
    setGuestForm(emptyGuestForm);
    setGuestDialogOpen(true);
  };

  const handleCreateGuest = async (e) => {
    e.preventDefault();
    if (creatingGuest) return;
    setCreatingGuest(true);
    try {
      const res = await createGuest(guestForm);
      const newGuest = res.data;
      toast.success(`Guest "${newGuest.name}" added`);
      const g = await getGuests();
      setGuests(g.data);
      setGuestId(newGuest.id);
      setGuestDialogOpen(false);
      setGuestForm(emptyGuestForm);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create guest');
    } finally {
      setCreatingGuest(false);
    }
  };

  const handleBook = async () => {
    if (groupMode) {
      if (!guestId || roomIds.length < 2 || !checkIn || !checkOut) {
        toast.error('Pick a lead guest, at least 2 rooms, and dates');
        return;
      }
    } else {
      if (!guestId || !roomId || !checkIn || !checkOut) {
        toast.error('Please fill all fields');
        return;
      }
    }
    setSubmitting(true);
    try {
      if (groupMode) {
        const res = await createGroupBooking({
          guest_id: guestId,
          room_ids: roomIds,
          check_in_date: format(checkIn, 'yyyy-MM-dd'),
          check_out_date: format(checkOut, 'yyyy-MM-dd'),
          num_guests_per_room: parseInt(numGuestsPerRoom) || 1,
          coupon_code: couponCode || undefined,
          meal_plan: mealPlan,
          custom_rate: customRate !== '' ? parseFloat(customRate) : undefined,
          group_name: groupName.trim() || undefined,
        });
        const d = res.data;
        toast.success(`${d.group_name} booked: ${d.rooms_booked} rooms · ₹${d.grand_total.toLocaleString('en-IN')}`);
      } else {
        await createBooking({
          guest_id: guestId,
          room_id: roomId,
          check_in_date: format(checkIn, 'yyyy-MM-dd'),
          check_out_date: format(checkOut, 'yyyy-MM-dd'),
          num_guests: 1,
          special_requests: '',
          coupon_code: couponCode || undefined,
          meal_plan: mealPlan,
          custom_rate: customRate !== '' ? parseFloat(customRate) : undefined,
        });
        toast.success('Booking created!');
      }
      reset();
      loadOptions();
      onBooked?.();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create booking');
    } finally {
      setSubmitting(false);
    }
  };

  const selectedRoomLabels = roomIds
    .map(id => rooms.find(r => r.id === id))
    .filter(Boolean)
    .map(r => r.room_number);

  return (
    <div className="bg-white border border-stone-200 rounded-md p-4 mb-8 animate-fade-in" data-testid="quick-booking-bar">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <div className="p-1.5 bg-sage-100 rounded">
            <Zap className="w-4 h-4 text-sage-700" />
          </div>
          <h2 className="font-semibold text-stone-900 font-['Manrope'] text-sm">Quick Booking</h2>
        </div>
        <label className="flex items-center gap-2 text-xs text-stone-600 cursor-pointer select-none" data-testid="group-mode-label">
          <Users className={`w-3.5 h-3.5 ${groupMode ? 'text-sage-700' : 'text-stone-400'}`} />
          <span className={groupMode ? 'text-sage-700 font-medium' : ''}>Group Booking</span>
          <Switch checked={groupMode} onCheckedChange={toggleGroupMode} data-testid="group-mode-switch" />
        </label>
      </div>

      <div className="flex flex-col lg:flex-row items-stretch lg:items-end gap-3 flex-wrap">
        {/* Guest */}
        <div className="flex-1 min-w-[200px]">
          <div className="flex items-center justify-between mb-1">
            <label className="text-xs font-medium text-stone-500">{groupMode ? 'Lead Guest' : 'Guest'}</label>
            <button
              type="button"
              onClick={openNewGuestDialog}
              className="text-xs text-sage-700 hover:text-sage-800 font-medium flex items-center gap-0.5"
              data-testid="quick-book-new-guest-btn"
            >
              <Plus className="w-3 h-3" />New
            </button>
          </div>
          {guests.length > 0 ? (
            <Select value={guestId} onValueChange={setGuestId}>
              <SelectTrigger className="border-stone-300 h-9 text-sm" data-testid="quick-book-guest">
                <SelectValue placeholder="Select guest" />
              </SelectTrigger>
              <SelectContent className="bg-white">
                {guests.map(g => (
                  <SelectItem key={g.id} value={g.id}>{g.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          ) : (
            <Button
              variant="outline" size="sm"
              className="w-full h-9 border-stone-300 text-stone-600 text-sm justify-start"
              onClick={openNewGuestDialog}
              data-testid="quick-book-add-first-guest"
            >
              <UserPlus className="w-3.5 h-3.5 mr-1.5" />Add your first guest
            </Button>
          )}
        </div>

        {/* Room(s) */}
        <div className="flex-1 min-w-[200px]">
          <label className="text-xs font-medium text-stone-500 mb-1 block">
            {groupMode ? `Rooms (${roomIds.length} selected)` : 'Room'}
          </label>
          {groupMode ? (
            <Popover>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  className="w-full h-9 justify-start border-stone-300 text-sm font-normal truncate"
                  data-testid="quick-book-rooms-multi"
                >
                  {roomIds.length === 0 ? 'Pick 2+ rooms' : selectedRoomLabels.join(', ')}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-72 p-0 bg-white" align="start">
                <div className="max-h-64 overflow-y-auto p-2 space-y-1">
                  {rooms.length === 0 && (
                    <div className="text-sm text-stone-500 p-3 text-center">No available rooms</div>
                  )}
                  {rooms.map(r => {
                    const checked = roomIds.includes(r.id);
                    return (
                      <label
                        key={r.id}
                        className="flex items-center gap-2 p-2 rounded hover:bg-stone-50 cursor-pointer text-sm"
                        data-testid={`group-room-option-${r.id}`}
                      >
                        <Checkbox checked={checked} onCheckedChange={() => toggleRoomInGroup(r.id)} />
                        <span className="flex-1 truncate">
                          {r.room_number} — {r.room_type}
                          <span className="text-stone-400"> ₹{r.price_per_night}</span>
                        </span>
                      </label>
                    );
                  })}
                </div>
              </PopoverContent>
            </Popover>
          ) : (
            <Select value={roomId} onValueChange={setRoomId}>
              <SelectTrigger className="border-stone-300 h-9 text-sm" data-testid="quick-book-room">
                <SelectValue placeholder="Select room" />
              </SelectTrigger>
              <SelectContent className="bg-white">
                {rooms.map(r => (
                  <SelectItem key={r.id} value={r.id}>
                    {r.room_number} — {r.room_type} (₹{r.price_per_night})
                    {r.status === 'occupied' && <span className="text-sage-600 text-xs"> · in use now</span>}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
        </div>

        {/* Check-in */}
        <div className="min-w-[130px]">
          <label className="text-xs font-medium text-stone-500 mb-1 block">Check-in</label>
          <Popover>
            <PopoverTrigger asChild>
              <Button variant="outline" className="w-full h-9 justify-start border-stone-300 text-sm font-normal" data-testid="quick-book-checkin">
                <CalendarIcon className="w-3.5 h-3.5 mr-1.5 text-stone-400" />
                {checkIn ? format(checkIn, 'MMM d') : 'Date'}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0 bg-white" align="start">
              <Calendar mode="single" selected={checkIn} onSelect={handleCheckInChange} defaultMonth={checkIn || new Date()} initialFocus />
            </PopoverContent>
          </Popover>
        </div>

        {/* Check-out */}
        <div className="min-w-[130px]">
          <label className="text-xs font-medium text-stone-500 mb-1 block">Check-out</label>
          <Popover>
            <PopoverTrigger asChild>
              <Button variant="outline" className="w-full h-9 justify-start border-stone-300 text-sm font-normal" data-testid="quick-book-checkout">
                <CalendarIcon className="w-3.5 h-3.5 mr-1.5 text-stone-400" />
                {checkOut ? format(checkOut, 'MMM d') : 'Date'}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0 bg-white" align="start">
              <Calendar mode="single" selected={checkOut} onSelect={setCheckOut} defaultMonth={checkOut || (checkIn ? addDays(checkIn, 1) : new Date())} disabled={(d) => checkIn && d <= checkIn} initialFocus />
            </PopoverContent>
          </Popover>
        </div>

        {/* Meal Plan */}
        <div className="min-w-[130px]">
          <label className="text-xs font-medium text-stone-500 mb-1 block">Meal Plan</label>
          <Select value={mealPlan} onValueChange={setMealPlan}>
            <SelectTrigger className="border-stone-300 h-9 text-sm" data-testid="quick-book-meal-plan">
              <SelectValue placeholder="Meals" />
            </SelectTrigger>
            <SelectContent className="bg-white">
              {MEAL_PLAN_OPTIONS.map(opt => (
                <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Group-specific: Guests per room */}
        {groupMode && (
          <div className="min-w-[90px]">
            <label className="text-xs font-medium text-stone-500 mb-1 block">Guests/Room</label>
            <Input
              type="number" min="1"
              value={numGuestsPerRoom}
              onChange={e => setNumGuestsPerRoom(e.target.value)}
              className="h-9 border-stone-300 text-sm"
              data-testid="quick-book-guests-per-room"
            />
          </div>
        )}

        {/* Coupon */}
        <div className="min-w-[100px]">
          <label className="text-xs font-medium text-stone-500 mb-1 block">Coupon</label>
          <Input value={couponCode} onChange={e => setCouponCode(e.target.value.toUpperCase())} placeholder="Code" className="h-9 border-stone-300 text-sm font-mono" data-testid="quick-book-coupon" />
        </div>

        {/* Custom Rate (admin) */}
        {isAdmin && (
          <div className="min-w-[110px]">
            <label className="text-xs font-medium text-stone-500 mb-1 flex items-center gap-1">
              <ShieldCheck className="w-3 h-3 text-sage-700" />Custom ₹
            </label>
            <Input
              type="number" min="0"
              value={customRate}
              onChange={e => setCustomRate(e.target.value)}
              placeholder="Override"
              className="h-9 border-stone-300 text-sm"
              data-testid="quick-book-custom-rate"
            />
          </div>
        )}

        {/* Book Now */}
        <Button
          onClick={handleBook}
          disabled={submitting || !guestId || !checkIn || !checkOut || (groupMode ? roomIds.length < 2 : !roomId)}
          className="bg-sage-700 hover:bg-sage-800 text-white h-9 px-5 text-sm whitespace-nowrap"
          data-testid="quick-book-submit"
        >
          {submitting ? 'Booking...' : groupMode ? `Book ${roomIds.length || ''} Rooms` : 'Book Now'}
        </Button>
      </div>

      {/* Group name input — appears only in group mode, as a second row */}
      {groupMode && (
        <div className="mt-3 flex items-center gap-3">
          <label className="text-xs font-medium text-stone-500 whitespace-nowrap">Group name</label>
          <Input
            value={groupName}
            onChange={e => setGroupName(e.target.value)}
            placeholder="e.g. Sharma Family Trip (optional — defaults to 'Group of [guest]')"
            className="h-8 border-stone-300 text-sm max-w-md"
            data-testid="quick-book-group-name"
          />
        </div>
      )}

      <GuestFormDialog
        open={guestDialogOpen}
        onOpenChange={setGuestDialogOpen}
        onSubmit={handleCreateGuest}
        formData={guestForm}
        setFormData={setGuestForm}
        editingGuest={null}
      />
    </div>
  );
};
